import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articles, type ArticleData } from '@/mocks/articles';
import { ArticleSeoJsonLd } from '@/components/feature/SeoJsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.autos';

function ArticleNotFound() {
  return (
    <div className="pt-28 pb-20 text-center">
      <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
        <i className="ri-file-unknow-line text-accent-600 text-xl"></i>
      </div>
      <h1 className="text-2xl font-bold text-foreground-900 mb-3">Article Not Found</h1>
      <p className="text-foreground-600 text-sm mb-6">The article you are looking for does not exist or has been moved.</p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
      >
        Back to Vigorbuy Spreadsheet Home
        <i className="ri-home-4-line"></i>
      </Link>
    </div>
  );
}

function ArticleContent({ article }: { article: ArticleData }) {
  return (
    <article className="pt-24 pb-14 md:pt-28 md:pb-20 bg-background-50">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              <li className="text-foreground-700 font-medium">{article.category}</li>
            </ol>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 mb-4 text-xs font-medium bg-accent-100 text-accent-700 rounded-full">
              {article.category}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              {article.intro}
            </p>
          </header>

          <img
            src={article.heroImage}
            alt={article.title}
            className="w-full rounded-lg object-cover h-48 md:h-72 object-top mb-10"
          />

          <div className="space-y-10">
            {article.sections.map((section, sIdx) => (
              <section key={sIdx}>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {section.heading}
                </h2>

                {section.paragraphs?.map((p, pIdx) => (
                  <p key={pIdx} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                    {p}
                  </p>
                ))}

                {section.steps && (
                  <div className="space-y-4 mt-5">
                    {section.steps.map((step, stIdx) => (
                      <div key={stIdx} className="flex gap-4 p-4 md:p-5 bg-background-100 rounded-lg border border-background-200/70">
                        <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-600 font-bold text-sm">{stIdx + 1}</span>
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-1.5">
                            {step.title}
                          </h3>
                          <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {section.tips && (
                  <div className="space-y-3 mt-5">
                    {section.tips.map((tip, tIdx) => (
                      <div key={tIdx} className="p-4 md:p-5 bg-accent-50/50 rounded-lg border border-accent-100">
                        <div className="flex items-start gap-3">
                          <div className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-lightbulb-line text-accent-600 text-xs"></i>
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{tip.title}</h3>
                            <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">{tip.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {section.table && (
                  <div className="overflow-x-auto mt-5 mb-2">
                    <table className="w-full border-collapse bg-background-50 rounded-lg overflow-hidden border border-background-200/70">
                      <thead>
                        <tr className="bg-background-100">
                          <th className="px-5 py-3 text-left text-sm font-semibold text-foreground-800 border-b border-background-200/70">Method</th>
                          <th className="px-5 py-3 text-left text-sm font-semibold text-foreground-800 border-b border-background-200/70">Characteristics</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.map((row, rIdx) => (
                          <tr key={rIdx} className={rIdx < section.table!.length - 1 ? 'border-b border-background-100' : ''}>
                            <td className="px-5 py-3 text-sm text-foreground-600">{row.left}</td>
                            <td className="px-5 py-3 text-sm text-foreground-700 font-medium">{row.right}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </div>

          <div className="mt-10 p-5 md:p-7 bg-primary-50/50 rounded-lg border border-primary-100">
            <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-3">
              Start Your Product Discovery Journey
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-5">
              Now that you have read this guide, the next step is simple: pick a category and start exploring. The organized product discovery system is ready when you are.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/#categories"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Browse Categories
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-background-50 border border-background-300/60 text-foreground-800 rounded-md font-semibold text-sm hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                Back to Home
                <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-3">Related Resources</h3>
            <div className="flex flex-wrap gap-2">
              {article.relatedLinks.map((link) => (
                <Link
                  key={link.text}
                  to={link.url}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-background-100 border border-background-200/70 rounded-full text-xs text-foreground-600 hover:text-primary-500 hover:border-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.text}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const effectiveSlug = slug || 'vigorbuy-spreadsheet-guide';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [effectiveSlug]);

  const article = articles.find((a) => a.slug === effectiveSlug);

  return (
    <main>
      {article && (
        <ArticleSeoJsonLd
          headline={article.title}
          description={article.metaDescription}
          image={article.heroImage}
          datePublished="2026-07-15"
          dateModified="2026-08-02"
          articleUrl={`${SITE_URL}/article/${article.slug}`}
        />
      )}
      <Navbar />
      {article ? <ArticleContent article={article} /> : <ArticleNotFound />}
      <Footer />
    </main>
  );
}